import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageGuide } from "@/components/PageGuide";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import {
  CalendarDays, Flame, FolderOpen, Sparkles, Check, BellRing, GraduationCap,
  LayoutGrid, List, CalendarRange, Sun, Grid3x3,
} from "lucide-react";
import { toast } from "sonner";
import { CalendarToolbar } from "@/components/calendar/CalendarToolbar";
import { MonthGrid } from "@/components/calendar/MonthGrid";
import { WeekStrip } from "@/components/calendar/WeekStrip";
import { AgendaList } from "@/components/calendar/AgendaList";
import { DayView } from "@/components/calendar/DayView";
import { YearView } from "@/components/calendar/YearView";
import { DayPanel } from "@/components/calendar/DayPanel";
import { TaskList } from "@/components/calendar/TaskList";
import { ShareCalendar } from "@/components/calendar/ShareCalendar";
import { GoogleSync } from "@/components/calendar/GoogleSync";
import { CalendarSettings } from "@/components/calendar/CalendarSettings";
import { ThemePicker } from "@/components/calendar/ThemePicker";
import { useAccent } from "@/context/AccentContext";
import { WeatherStrip } from "@/components/calendar/WeatherStrip";
import { RainCheck } from "@/components/calendar/RainCheck";
import { HourlyRain } from "@/components/calendar/HourlyRain";
import { PackingNudge } from "@/components/calendar/PackingNudge";
import {
  fmtTime, leadingBlanks, ALERT_MINUTES,
} from "@/components/calendar/palette";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const VIEWS = {
  day: { label: "Day", Icon: Sun },
  week: { label: "Week", Icon: CalendarRange },
  month: { label: "Month", Icon: LayoutGrid },
  year: { label: "Year", Icon: Grid3x3 },
  agenda: { label: "Agenda", Icon: List },
};

const blankEvent = {
  title: "", start: "", end: "", all_day: true, colour: "blue",
  notes: "", location: "", repeat: "none", alert: "none", tz: "", outdoors: false,
  remind: { lead: "none", time: "09:00", at: "" },
};

const DEFAULTS = {
  theme: "electric", holiday_country: "", timezone: "", week_starts: "mon",
  clock: "24", density: "roomy", show_holidays: true, mobile: "",
};

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monthKeyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthName = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
const shiftDay = (day, by) => {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + by);
  return iso(d);
};
const weekRangeLabel = (days) => {
  const opts = { day: "numeric", month: "short" };
  return `${new Date(`${days[0]}T12:00:00`).toLocaleDateString(undefined, opts)} – ${new Date(
    `${days[6]}T12:00:00`
  ).toLocaleDateString(undefined, opts)}`;
};

export default function Calendar() {
  const { theme: appTheme, accent: appAccent, saveTheme } = useAccent();
  const today = useMemo(() => new Date(), []);
  const todayISO = iso(today);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const month = monthKeyOf(cursor);
  const thisMonth = month === monthKeyOf(today);

  const [settings, setSettings] = useState(DEFAULTS);
  const [countries, setCountries] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [activity, setActivity] = useState({});
  const [letters, setLetters] = useState({});
  const [streak, setStreak] = useState(0);
  const [reminders, setReminders] = useState([]);
  const [due, setDue] = useState([]);
  const [plan, setPlan] = useState(null);
  const [picked, setPicked] = useState(null);

  // /calendar?day=YYYY-MM-DD opens straight onto that day (weekend suggestions link here).
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("day");
    if (wanted && /^\d{4}-\d{2}-\d{2}$/.test(wanted)) {
      setPicked(wanted);
      setCursor(new Date(`${wanted}T12:00:00`));
    }
  }, []);
  const [draft, setDraft] = useState("");
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(blankEvent);
  const [editingId, setEditingId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [emailOn, setEmailOn] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [place, setPlace] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [forecastStale, setForecastStale] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(todayISO);
  const [rangeEvents, setRangeEvents] = useState([]);
  const [soonReminders, setSoonReminders] = useState([]);
  const [importing, setImporting] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [shares, setShares] = useState({ shared_with: [], shared_with_me: [], public: { on: false } });
  const [showShared, setShowShared] = useState(false);
  const [uploading, setUploading] = useState("");
  const [google, setGoogle] = useState({ configured: false, connected: false, email: "" });
  const [googleBusy, setGoogleBusy] = useState(false);
  const [travel, setTravel] = useState([]);
  const [rain, setRain] = useState([]);
  const [hourlyDay, setHourlyDay] = useState(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return iso(t);
  });
  const [hourly, setHourly] = useState(null);
  const [packing, setPacking] = useState(null);
  const [dryWindows, setDryWindows] = useState([]);
  const [moving, setMoving] = useState("");
  const fileInput = useRef(null);
  const timers = useRef([]);

  const accent = appAccent;

  const saveAccent = async (next) => {
    try {
      await saveTheme(next);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that colour");
    }
  };

  const loadReminders = useCallback(
    () =>
      api
        .get("/calendar/reminders", { params: { month } })
        .then(({ data }) => {
          setReminders(data.reminders || []);
          setDue(data.due || []);
        })
        .catch((e) => console.error("could not load reminders", e)),
    [month]
  );

  const loadPlan = useCallback(
    () =>
      api
        .get("/calendar/practice-plan", { params: { month } })
        .then(({ data }) => setPlan(data))
        .catch((e) => console.error("could not load the practice plan", e)),
    [month]
  );

  const loadEvents = useCallback(
    () =>
      api
        .get("/calendar/events", { params: { month, shared: showShared } })
        .then(({ data }) => setEvents(data.events || []))
        .catch((e) => console.error("could not load events", e)),
    [month, showShared]
  );

  const loadRange = useCallback(
    (start, days) =>
      api
        .get("/calendar/events", { params: { start, days, shared: showShared } })
        .then(({ data }) => setRangeEvents(data.events || []))
        .catch((e) => console.error("could not load that stretch of days", e)),
    [showShared]
  );

  const loadTasks = useCallback(
    () =>
      api
        .get("/calendar/tasks")
        .then(({ data }) => {
          setTasks(data.tasks || []);
          setOverdue(data.overdue || []);
        })
        .catch((e) => console.error("could not load your list", e)),
    []
  );

  const loadShares = useCallback(
    () =>
      api
        .get("/calendar/shares")
        .then(({ data }) => setShares(data))
        .catch((e) => console.error("could not load sharing", e)),
    []
  );

  const loadRain = useCallback(
    () =>
      api
        .get("/calendar/rain-check")
        .then(({ data }) => setRain(data.warnings || []))
        .catch((e) => console.error("could not check the rain", e)),
    []
  );

  const loadGoogle = useCallback(
    () =>
      api
        .get("/calendar/google/status")
        .then(({ data }) => setGoogle(data))
        .catch((e) => console.error("could not check Google", e)),
    []
  );

  const loadWeather = useCallback((params) => {
    setWeatherLoading(true);
    return api
      .get("/calendar/weather", { params })
      .then(({ data }) => {
        setPlace(data.place || null);
        setForecast(data.days || []);
        setForecastStale(Boolean(data.stale));
        return data;
      })
      .catch((e) => {
        toast.error(apiError(e.response?.data?.detail) || "Could not fetch the forecast");
        return null;
      })
      .finally(() => setWeatherLoading(false));
  }, []);

  useEffect(() => {
    loadReminders();
    loadPlan();
    loadEvents();
    loadRain();
  }, [loadReminders, loadPlan, loadEvents, loadRain]);

  const moveEventTo = async (warning, day) => {
    setMoving(warning.event_id);
    try {
      await api.patch(`/calendar/events/${warning.event_id}/day`, null, { params: { day } });
      await loadEvents();
      await loadRain();
      toast.success(`${warning.title} moved to ${day}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not move that event");
    } finally {
      setMoving("");
    }
  };

  useEffect(() => {
    loadTasks();
    loadShares();
    loadGoogle();
    api
      .get("/calendar/countries")
      .then(({ data }) => setCountries(data.countries || []))
      .catch((e) => console.error("could not load the country list", e));
  }, [loadTasks, loadShares, loadGoogle]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("google");
    if (!flag) return;
    if (flag === "connected") toast.success("Google Calendar connected");
    if (flag === "failed") {
      const why = params.get("why");
      toast.error(
        why === "access_denied"
          ? "Google says this app is still in testing — add your Google address under OAuth consent screen → Test users, then try again."
          : "Google would not finish that — try connecting again",
        { duration: 12000 }
      );
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .get("/calendar/settings")
      .then(async ({ data }) => {
        if (!alive) return;
        setSettings({ ...DEFAULTS, ...data });
        if (!data.timezone) {
          const guess = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (guess) {
            api
              .put("/calendar/settings", { timezone: guess })
              .then(({ data: saved }) => {
                setSettings({ ...DEFAULTS, ...saved });
                toast.success(`Time zone set to ${guess.replace(/_/g, " ")}`);
              })
              .catch((e) => console.error("could not save your time zone", e));
          }
        }
        if (data.place) {
          await loadWeather({});
          return;
        }
        if (!navigator.geolocation) {
          setWeatherLoading(false);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => loadWeather({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
          () => setWeatherLoading(false),
          { timeout: 8000 }
        );
      })
      .catch(() => setWeatherLoading(false));
    return () => {
      alive = false;
    };
  }, [loadWeather]);

  useEffect(() => {
    if (!settings.holiday_country || !settings.show_holidays) {
      setHolidays({});
      return;
    }
    api
      .get("/calendar/holidays", { params: { year: cursor.getFullYear() } })
      .then(({ data }) =>
        setHolidays(Object.fromEntries((data.holidays || []).map((h) => [h.day, h.name]))))
      .catch((e) => console.error("could not load holidays", e));
  }, [settings.holiday_country, settings.show_holidays, cursor]);

  useEffect(() => {
    if (!thisMonth) {
      setActivity({});
      return;
    }
    api
      .get("/prompts/recap", { params: { days: today.getDate() } })
      .then(({ data }) => {
        setActivity(Object.fromEntries(data.by_day.map((d) => [d.day, d.count])));
        setStreak(data.streak || 0);
      })
      .catch((e) => console.error("could not load activity", e));
  }, [thisMonth, today]);

  useEffect(() => {
    api
      .get("/letters")
      .then(({ data }) =>
        setLetters(
          (data.letters || []).reduce((acc, l) => {
            const day = String(l.created_at).slice(0, 10);
            acc[day] = (acc[day] || 0) + 1;
            return acc;
          }, {})
        ))
      .catch((e) => console.error("could not load letters", e));
  }, []);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setHits(null);
      return;
    }
    const timer = setTimeout(() => {
      api
        .get("/calendar/search", { params: { q: term } })
        .then(({ data }) => setHits(data))
        .catch((e) => console.error("could not search", e));
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    api
      .get("/calendar/hourly", { params: { day: hourlyDay } })
      .then(({ data }) => setHourly(data))
      .catch((e) => console.error("could not load the hourly forecast", e));
  }, [hourlyDay, place]);

  useEffect(() => {
    api
      .get("/calendar/packing", { params: { day: hourlyDay } })
      .then(({ data }) => setPacking(data))
      .catch((e) => console.error("could not work out what to take", e));
  }, [hourlyDay, place, events]);

  useEffect(() => {
    if (!picked || !form.outdoors) {
      setDryWindows([]);
      return;
    }
    api
      .get("/calendar/hourly", { params: { day: picked } })
      .then(({ data }) => setDryWindows(data.windows || []))
      .catch((e) => console.error("could not find the dry hours", e));
  }, [picked, form.outdoors]);

  const hourlyDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => shiftDay(todayISO, i)),
    [todayISO]
  );

  const weatherMap = useMemo(
    () => Object.fromEntries(forecast.map((d) => [d.day, d])),
    [forecast]
  );

  const weekDays = useMemo(() => {
    const d = new Date(`${anchor}T12:00:00`);
    const back = settings.week_starts === "sun" ? d.getDay() : (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - back);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(d);
      day.setDate(d.getDate() + i);
      return iso(day);
    });
  }, [anchor, settings.week_starts]);

  useEffect(() => {
    if (view === "week") loadRange(weekDays[0], 7);
    if (view === "agenda") loadRange(todayISO, 60);
    if (view === "day") loadRange(anchor, 1);
    if (view === "year") loadRange(`${cursor.getFullYear()}-01-01`, 120);
  }, [view, weekDays, anchor, cursor, todayISO, loadRange, events]);

  useEffect(() => {
    if (view !== "agenda") return;
    const next = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    api
      .get("/calendar/reminders", { params: { month: monthKeyOf(next) } })
      .then(({ data }) => setSoonReminders(data.reminders || []))
      .catch((e) => console.error("could not load next month's reminders", e));
  }, [view, today]);

  const shownEvents = view === "month" ? events : rangeEvents;
  const shownReminders = view === "agenda" ? [...reminders, ...soonReminders] : reminders;

  // Enough time to get from one place to the next?
  const focusDay = view === "day" ? anchor : picked;
  useEffect(() => {
    if (!focusDay) {
      setTravel([]);
      return;
    }
    const busy = [...events, ...rangeEvents].filter(
      (e) => e.day === focusDay && !e.all_day && e.location);
    if (busy.length < 2) {
      setTravel([]);
      return;
    }
    api
      .get("/calendar/travel", { params: { day: focusDay } })
      .then(({ data }) => setTravel(data.warnings || []))
      .catch((e) => console.error("could not check travel time", e));
  }, [focusDay, events, rangeEvents]);

  // Browser alerts for anything starting soon, while this page is open.
  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const soon = [...events, ...rangeEvents].filter(
      (e) => e.day === todayISO && !e.all_day && ALERT_MINUTES[e.alert] !== undefined
    );
    soon.forEach((ev) => {
      const at = new Date(`${ev.day}T${ev.start}:00`).getTime() - ALERT_MINUTES[ev.alert] * 60000;
      const wait = at - Date.now();
      if (wait <= 0 || wait > 6 * 60 * 60 * 1000) return;
      timers.current.push(
        setTimeout(() => {
          new Notification(ev.title, {
            body: `${fmtTime(ev.start, settings.clock)}${ev.location ? ` · ${ev.location}` : ""}`,
          });
        }, wait)
      );
    });
    return () => timers.current.forEach(clearTimeout);
  }, [events, rangeEvents, todayISO, settings.clock]);

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const lead = leadingBlanks(first, settings.week_starts);
    return [
      ...Array.from({ length: lead }, () => null),
      ...Array.from({ length: daysInMonth }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1)),
    ];
  }, [cursor, settings.week_starts]);

  const busyDays = useMemo(
    () => rangeEvents.reduce((acc, e) => ({ ...acc, [e.day]: (acc[e.day] || 0) + 1 }), {}),
    [rangeEvents]
  );

  const pickDay = (key) => {
    setPicked(key);
    setAnchor(key);
    setDraft("");
    setForm(blankEvent);
    setEditingId(null);
  };

  const jumpTo = (day) => {
    setCursor(new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, 1));
    setPicked(day);
    setAnchor(day);
    setQuery("");
    setHits(null);
  };

  const shift = (by) => {
    if (view === "day") {
      const next = shiftDay(anchor, by);
      setAnchor(next);
      setCursor(new Date(Number(next.slice(0, 4)), Number(next.slice(5, 7)) - 1, 1));
      return;
    }
    if (view === "week") {
      const next = shiftDay(weekDays[0], by * 7);
      setAnchor(next);
      setCursor(new Date(Number(next.slice(0, 4)), Number(next.slice(5, 7)) - 1, 1));
      return;
    }
    if (view === "year") {
      setCursor((c) => new Date(c.getFullYear() + by, c.getMonth(), 1));
      return;
    }
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + by, 1));
    setPicked(null);
    setForm(blankEvent);
    setEditingId(null);
    setIdeas([]);
  };

  const backToToday = () => {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setAnchor(todayISO);
    setPicked(todayISO);
  };

  const saveSettings = async (patch) => {
    const before = settings;
    setSettings((s) => ({ ...s, ...patch }));
    try {
      const { data } = await api.put("/calendar/settings", patch);
      setSettings({ ...DEFAULTS, ...data });
    } catch (e) {
      setSettings(before);
      toast.error(apiError(e.response?.data?.detail) || "Could not save that setting");
    }
  };

  const changePlace = async (city) => {
    if (city === null) {
      setForecast([]);
      setPlace(null);
      return;
    }
    const data = await loadWeather({ city });
    if (data?.days?.length) toast.success(`Forecast for ${data.place?.name || city}`);
  };

  const loadIdeas = async () => {
    setIdeasLoading(true);
    try {
      const { data } = await api.get("/calendar/event-ideas", { params: { month } });
      setIdeas(data.ideas || []);
      if (!data.ideas?.length) toast.error("No ideas came back — try again in a moment");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not fetch ideas");
    } finally {
      setIdeasLoading(false);
    }
  };

  const saveEvent = async () => {
    if (!form.title.trim() || !picked) {
      toast.error("Give the event a name");
      return;
    }
    const body = { ...form, title: form.title.trim(), day: picked };
    try {
      if (editingId) await api.put(`/calendar/events/${editingId}`, body);
      else await api.post("/calendar/events", body);
      setForm(blankEvent);
      setEditingId(null);
      await loadEvents();
      if (ALERT_MINUTES[body.alert] !== undefined && typeof Notification !== "undefined"
          && Notification.permission === "default") {
        Notification.requestPermission();
      }
      toast.success(editingId ? "Event updated" : "Event added");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that event");
    }
  };

  const editEvent = (ev) => {
    setEditingId(ev.event_id);
    setForm({
      title: ev.title, start: ev.start, end: ev.end, all_day: ev.all_day,
      colour: ev.colour, notes: ev.notes, location: ev.location || "",
      repeat: ev.repeat || "none", alert: ev.alert || "none", tz: ev.tz || "",
      outdoors: Boolean(ev.outdoors),
      remind: { lead: ev.remind_lead || "none", time: ev.remind_time || "09:00",
                at: ev.remind_custom || "" },
    });
  };

  const removeEvent = async (ev) => {
    await api.delete(`/calendar/events/${ev.event_id}`);
    await loadEvents();
  };

  const dropEvent = async (day) => {
    if (!dragId) return;
    const id = dragId;
    setDragId(null);
    try {
      await api.post(`/calendar/events/${id}/copy`, null, { params: { day } });
      await loadEvents();
      toast.success("Copied to that day");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not copy that event");
    }
  };

  const exportIcs = async () => {
    try {
      const { data } = await api.get("/calendar/export.ics", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "chatmilu-calendar.ics";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Calendar file saved — open it in Apple Calendar, Outlook or Google");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not export your calendar");
    }
  };

  const importIcs = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImporting(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/calendar/import", body);
      await loadEvents();
      toast.success(
        `${data.added} event${data.added === 1 ? "" : "s"} brought in` +
          (data.skipped ? ` · ${data.skipped} already here` : "")
      );
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not read that calendar file");
    } finally {
      setImporting(false);
    }
  };

  const uploadFile = async (ev, file) => {
    setUploading(ev.event_id);
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post(`/calendar/events/${ev.event_id}/attachments`, body);
      await loadEvents();
      toast.success(`${file.name} kept with this event`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not attach that file");
    } finally {
      setUploading("");
    }
  };

  const openFile = async (att) => {
    try {
      const { data } = await api.get(`/calendar/attachments/${att.att_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not open that file");
    }
  };

  const deleteFile = async (ev, att) => {
    try {
      await api.delete(`/calendar/events/${ev.event_id}/attachments/${att.att_id}`);
      await loadEvents();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not remove that file");
    }
  };

  const addTask = async (body) => {
    try {
      await api.post("/calendar/tasks", body);
      await loadTasks();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not add that task");
    }
  };

  const patchTask = async (task, patch) => {
    try {
      await api.patch(`/calendar/tasks/${task.task_id}`, patch);
      await loadTasks();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not change that task");
    }
  };

  const deleteTask = async (task) => {
    await api.delete(`/calendar/tasks/${task.task_id}`);
    await loadTasks();
  };

  const share = async (email, canEdit) => {
    try {
      await api.post("/calendar/shares", { email, can_edit: canEdit });
      await loadShares();
      toast.success(`Shared with ${email}`);
      return true;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not share with them");
      return false;
    }
  };

  const unshare = async (s) => {
    await api.delete(`/calendar/shares/${s.share_id}`);
    await loadShares();
  };

  const togglePublic = async (on) => {
    try {
      await api.put("/calendar/public-link", { on });
      await loadShares();
      toast.success(on ? "Your read-only link is live" : "Link switched off");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not change that link");
    }
  };

  const connectGoogle = async () => {
    setGoogleBusy(true);
    try {
      const { data } = await api.get("/calendar/google/connect", {
        params: { base: window.location.origin },
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not open Google");
    } finally {
      setGoogleBusy(false);
    }
  };

  const pullGoogle = async () => {
    setGoogleBusy(true);
    try {
      const { data } = await api.post("/calendar/google/pull");
      await loadEvents();
      toast.success(`${data.added} brought in${data.updated ? ` · ${data.updated} refreshed` : ""}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not fetch your Google events");
    } finally {
      setGoogleBusy(false);
    }
  };

  const pushGoogle = async () => {
    setGoogleBusy(true);
    try {
      const { data } = await api.post("/calendar/google/push");
      toast.success(`${data.pushed} sent up to Google${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send those to Google");
    } finally {
      setGoogleBusy(false);
    }
  };

  const disconnectGoogle = async () => {
    await api.delete("/calendar/google");
    await loadGoogle();
    toast.success("Google Calendar disconnected");
  };

  const setGoogleAuto = async (on) => {
    setGoogle((g) => ({ ...g, auto_sync: on }));
    try {
      await api.put("/calendar/google/auto", { on });
      toast.success(on ? "I'll keep them in step every hour" : "Hourly syncing turned off");
    } catch (e) {
      setGoogle((g) => ({ ...g, auto_sync: !on }));
      toast.error(apiError(e.response?.data?.detail) || "Could not save that");
    }
  };

  const toggleEmails = async () => {
    const on = !emailOn;
    setEmailOn(on);
    try {
      await api.put("/calendar/reminder-emails", { on });
      toast.success(on ? "MiLu will email you on the morning one is due" : "Morning emails turned off");
    } catch (e) {
      setEmailOn(!on);
      toast.error(apiError(e.response?.data?.detail) || "Could not save that");
    }
  };

  const emailNow = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/calendar/reminders/email-now");
      toast.success(`Sent — ${data.reminders} ${data.reminders === 1 ? "reminder" : "reminders"} on their way`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send that email");
    } finally {
      setEmailing(false);
    }
  };

  const addReminder = async () => {
    const text = draft.trim();
    if (!text || !picked) return;
    try {
      await api.post("/calendar/reminders", { day: picked, text });
      setDraft("");
      await loadReminders();
      toast.success("MiLu will remind you on the day");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that reminder");
    }
  };

  const tick = async (r) => {
    await api.patch(`/calendar/reminders/${r.reminder_id}`, { done: !r.done });
    await loadReminders();
  };

  const removeReminder = async (r) => {
    await api.delete(`/calendar/reminders/${r.reminder_id}`);
    await loadReminders();
  };

  const togglePlanDay = async (index) => {
    const days = plan.days.includes(index)
      ? plan.days.filter((d) => d !== index)
      : [...plan.days, index];
    setPlan((p) => ({ ...p, days }));
    try {
      const { data } = await api.put("/calendar/practice-plan", { days });
      setPlan(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save your plan");
      loadPlan();
    }
  };

  const title = view === "week" ? weekRangeLabel(weekDays)
    : view === "day" ? new Date(`${anchor}T12:00:00`).toLocaleDateString(undefined,
        { weekday: "long", day: "numeric", month: "long" })
    : view === "year" ? String(cursor.getFullYear())
    : view === "agenda" ? "What's coming"
    : monthName(cursor);

  const offToday = view === "week" ? !weekDays.includes(todayISO)
    : view === "day" ? anchor !== todayISO
    : view === "year" ? cursor.getFullYear() !== today.getFullYear()
    : !thisMonth;

  return (
    <div className="cml-theme mx-auto max-w-4xl px-5 py-12 sm:px-8" style={{ "--theme": accent }}
         data-testid="calendar-page">
      <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: accent }}>Calendar</p>

      <CalendarToolbar
        title={title}
        views={VIEWS}
        view={view}
        setView={setView}
        accent={accent}
        offToday={offToday}
        hideNav={view === "agenda"}
        onShift={shift}
        onToday={backToToday}
        query={query}
        setQuery={setQuery}
        hits={hits}
        onJump={jumpTo}
        fileInput={fileInput}
        onImport={importIcs}
        onExport={exportIcs}
        importing={importing}
      />

      <p className="mt-3 text-sm text-ink-soft">
        Every day you used MiLu, the letters you kept and everything you have coming up.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {streak > 0 && (
          <span
            data-testid="calendar-streak"
            className="inline-flex items-center gap-1.5 rounded-full border border-orange-400/40 bg-orange-400/10 px-3 py-1.5 text-xs font-semibold text-orange-300"
          >
            <Flame className="h-3.5 w-3.5" /> {streak}-day streak
          </span>
        )}
        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-soft">
          <Sparkles className="h-3.5 w-3.5" style={{ color: accent }} /> Prompt opened
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-soft">
          <FolderOpen className="h-3.5 w-3.5 text-forest" /> Letter kept
        </span>
      </div>

      <ThemePicker theme={appTheme} onPick={saveAccent} label="My colour"
                   note="follows you across ChatMiLu" />

      <CalendarSettings
        settings={settings}
        countries={countries}
        accent={accent}
        onChange={saveSettings}
      />

      <WeatherStrip
        place={place}
        days={forecast}
        accent={accent}
        loading={weatherLoading}
        stale={forecastStale}
        onCity={changePlace}
      />

      <RainCheck warnings={rain} accent={accent} moving={moving} onMove={moveEventTo} />

      <HourlyRain
        data={hourly}
        accent={accent}
        clock={settings.clock}
        day={hourlyDay}
        days={hourlyDays}
        onPickDay={setHourlyDay}
      />

      <PackingNudge data={packing} accent={accent} />

      <TaskList
        tasks={tasks}
        overdue={overdue}
        accent={accent}
        todayISO={todayISO}
        onAdd={addTask}
        onPatch={patchTask}
        onDelete={deleteTask}
      />

      <ShareCalendar
        shares={shares}
        accent={accent}
        showShared={showShared}
        onToggleShared={() => setShowShared((s) => !s)}
        onShare={share}
        onUnshare={unshare}
        onPublic={togglePublic}
      />

      <GoogleSync
        status={google}
        accent={accent}
        busy={googleBusy}
        onConnect={connectGoogle}
        onPull={pullGoogle}
        onPush={pushGoogle}
        onAuto={setGoogleAuto}
        onDisconnect={disconnectGoogle}
      />

      {due.length > 0 && (
        <div className="mt-5 rounded-2xl border p-4" data-testid="reminders-due"
             style={{ borderColor: `${accent}55`, backgroundColor: `${accent}1A` }}>
          <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest" style={{ color: accent }}>
            <BellRing className="h-3.5 w-3.5" /> Waiting on you
          </p>
          <div className="mt-2 space-y-2">
            {due.map((r) => (
              <div key={r.reminder_id} className="flex flex-wrap items-center gap-2 text-sm">
                <button
                  data-testid={`reminder-tick-${r.reminder_id}`}
                  onClick={() => tick(r)}
                  aria-label="Mark as done"
                  className="rounded-full border border-white/20 p-1 text-ink-soft hover:text-ink"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <span dir="auto">{r.text}</span>
                <span className="text-xs text-ink-soft">{r.day}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-panel p-4"
           data-testid="reminder-email-settings">
        <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest" style={{ color: accent }}>
          <BellRing className="h-3.5 w-3.5" /> Morning emails
        </p>
        <button
          data-testid="reminder-email-toggle"
          onClick={toggleEmails}
          className="rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors"
          style={emailOn
            ? { borderColor: accent, color: accent, backgroundColor: `${accent}22` }
            : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
        >
          {emailOn ? "On" : "Off"}
        </button>
        <p className="text-xs text-ink-soft">I'll email you the morning a reminder or an event alert is due.</p>
        {due.length > 0 && (
          <button
            data-testid="reminder-email-now"
            onClick={emailNow}
            disabled={emailing}
            className="ms-auto rounded-full border border-white/10 px-4 py-1.5 text-xs font-semibold text-ink-soft transition-colors hover:text-ink"
          >
            {emailing ? "Sending…" : "Email me now"}
          </button>
        )}
      </div>

      {plan && (
        <div className="mt-5 rounded-2xl border border-white/10 bg-panel p-4" data-testid="practice-plan">
          <div className="flex flex-wrap items-center gap-3">
            <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-forest">
              <GraduationCap className="h-3.5 w-3.5" /> My practice days
            </p>
            {plan.planned_count > 0 && (
              <p className="text-xs text-ink-soft" data-testid="practice-plan-score">
                {plan.kept} of {plan.planned_count} kept this month · {plan.percent}%
              </p>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {WEEKDAYS.map((label, index) => (
              <button
                key={label}
                data-testid={`practice-day-${index}`}
                onClick={() => togglePlanDay(index)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  plan.days.includes(index)
                    ? "border-forest bg-forest/15 text-forest"
                    : "border-white/10 text-ink-soft hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {plan.days.length === 0 && (
            <p className="mt-2 text-xs text-ink-soft">
              Pick the days you want to practise and MiLu will track how you are doing.
            </p>
          )}
        </div>
      )}

      {view === "month" && (
        <MonthGrid
          cells={cells}
          todayISO={todayISO}
          picked={picked}
          onPick={pickDay}
          events={events}
          reminders={reminders}
          plan={plan}
          activity={activity}
          letters={letters}
          weather={weatherMap}
          holidays={holidays}
          accent={accent}
          clock={settings.clock}
          density={settings.density}
          weekStarts={settings.week_starts}
          dragId={dragId}
          setDragId={setDragId}
          onDrop={dropEvent}
        />
      )}

      {view === "week" && (
        <WeekStrip
          days={weekDays}
          events={rangeEvents}
          reminders={reminders}
          weather={weatherMap}
          holidays={holidays}
          accent={accent}
          clock={settings.clock}
          picked={picked}
          todayISO={todayISO}
          onPick={pickDay}
        />
      )}

      {view === "day" && (
        <DayView
          day={anchor}
          events={rangeEvents.filter((e) => e.day === anchor)}
          reminders={reminders.filter((r) => r.day === anchor)}
          tasks={tasks}
          holiday={holidays[anchor]}
          weather={weatherMap}
          accent={accent}
          clock={settings.clock}
          density={settings.density}
          travel={travel}
        />
      )}

      {view === "year" && (
        <YearView
          year={cursor.getFullYear()}
          busyDays={busyDays}
          holidays={holidays}
          todayISO={todayISO}
          accent={accent}
          weekStarts={settings.week_starts}
          onPickMonth={(m) => {
            setCursor(new Date(cursor.getFullYear(), m, 1));
            setView("month");
          }}
          onPickDay={(day) => {
            jumpTo(day);
            setView("day");
          }}
        />
      )}

      {view === "agenda" && (
        <AgendaList
          events={rangeEvents}
          reminders={shownReminders}
          tasks={tasks}
          holidays={holidays}
          weather={weatherMap}
          accent={accent}
          clock={settings.clock}
          todayISO={todayISO}
          onPick={pickDay}
        />
      )}

      {picked && (
        <DayPanel
          day={picked}
          events={shownEvents.filter((e) => e.day === picked)}
          reminders={reminders.filter((r) => r.day === picked)}
          holiday={holidays[picked]}
          accent={accent}
          clock={settings.clock}
          homeZone={settings.timezone}
          travel={travel}
          form={form}
          setForm={setForm}
          onSave={saveEvent}
          editingId={editingId}
          onEdit={editEvent}
          onDeleteEvent={removeEvent}
          ideas={ideas}
          ideasLoading={ideasLoading}
          onIdeas={loadIdeas}
          onClose={() => setPicked(null)}
          draft={draft}
          setDraft={setDraft}
          onAddReminder={addReminder}
          onTickReminder={tick}
          onDeleteReminder={removeReminder}
          uploading={uploading}
          onUpload={uploadFile}
          onOpenFile={openFile}
          onDeleteFile={deleteFile}
        />
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/prompts"
          data-testid="calendar-to-prompts"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/10 px-5 text-sm font-semibold text-ink-soft transition-colors hover:text-ink"
        >
          <CalendarDays className="h-4 w-4" /> See my week with MiLu
        </Link>
        <Link
          to="/phrasebook"
          data-testid="calendar-to-letters"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-white/10 px-5 text-sm font-semibold text-ink-soft transition-colors hover:text-ink"
        >
          <FolderOpen className="h-4 w-4" /> My Letters
        </Link>
      </div>

      <PageGuide name="calendar" />
    </div>
  );
}
